/* Moving an Event through its lifecycle — STORY-012.
 *
 * ── it prompts, it never blocks ─────────────────────────────────────────────
 * An earlier draft of the story guarded the moves: leaving `idea` would have
 * required a date and a Venue, `completed` would have been terminal. That was
 * rejected on 2026-08-10, because real planning does not work that way — an
 * event gets confirmed on a phone call before anybody books a room, and a tool
 * that argues with that gets worked around.
 *
 * So what is missing is SAID and the move still happens. The prompt below is
 * the whole of what replaced the guard: the information was always useful, it
 * just does not get a veto.
 *
 * ── one refusal, and it is billing ──────────────────────────────────────────
 * Committing to an Event consumes an active-Event entitlement. That is the only
 * no in this component, and it names the plan that lifts it rather than the
 * plan the workspace is on.
 *
 * ── a select, not five buttons ──────────────────────────────────────────────
 * Every status is reachable from every other, so this is a choice among five
 * values rather than a set of actions. `design.md` prefers a real `<select>`
 * where the behaviour permits one: keyboard-accessible, screen-reader correct,
 * and right on mobile for free. Five buttons would also imply a workflow, which
 * is precisely the reading the story spent its length removing.
 */

import type { Event, EventStatus } from '@eventuley/contracts';
import type { MessageKey } from '@eventuley/i18n';
import { useId, useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout } from '../../../shared/ui/index.js';
import { useTransitionEvent } from '../api/index.js';

/* Explicit, never `t(`events.status.${status}`)` — `i18n.md` forbids a
 * constructed key because it defeats extraction and unused-key detection. */
const LABELS = {
  idea: 'events.status.idea',
  planned: 'events.status.planned',
  confirmed: 'events.status.confirmed',
  completed: 'events.status.completed',
  cancelled: 'events.status.cancelled',
} as const satisfies Record<EventStatus, MessageKey>;

const ORDER: readonly EventStatus[] = ['idea', 'planned', 'confirmed', 'completed', 'cancelled'];

/* The plan's own name, capitalised for a sentence. Not a catalogue key: the
 * server names a plan CODE, and a future tier would have no key here. The same
 * helper and the same reasoning as the seat-limit refusal in Settings. */
const planLabel = (code: string) => code.charAt(0).toLocaleUpperCase('en-US') + code.slice(1);

/** The statuses at which an Event is a commitment rather than a thought. */
const COMMITTED: readonly EventStatus[] = ['planned', 'confirmed'];

export interface StatusControlProps {
  readonly event: Event;
  readonly organisationId: string;
  /** Today, at the Event's own zone. Injected so tests need no real clock. */
  readonly today: string;
}

export function StatusControl({ event, organisationId, today }: StatusControlProps) {
  const t = useTranslate();
  const id = useId();
  const move = useTransitionEvent(organisationId, event.id);

  const [chosen, setChosen] = useState<EventStatus>(event.status);

  /* Resynced when the Event moves underneath the screen.
   *
   * The initialiser runs once and this component is never remounted, so a
   * refetch used to leave the select showing a status the Event no longer had.
   * The badge above and the select then disagreed, and — worse —
   * `disabled={chosen === event.status}` flipped Save to ENABLED with nobody
   * touching anything. One click on a button that had armed itself sent
   * `{ from: <new>, to: <stale> }` and quietly undid somebody else's move.
   *
   * Reachable without any concurrency at all: saving the notes invalidates the
   * events query, so any move made elsewhere in between lands here. */
  const [seen, setSeen] = useState<EventStatus>(event.status);
  if (seen !== event.status) {
    setSeen(event.status);
    setChosen(event.status);
  }

  /* Derived from the CHOSEN status rather than the saved one, so the prompt
   * appears while somebody is deciding — which is when it can still change
   * anything — rather than after they have already moved it.
   *
   * **And only while a change is actually pending**, which is the half the
   * comment above claimed and the code did not do: once the move commits,
   * `chosen` becomes the saved status, so a Planned Event with no date went on
   * saying "worth knowing BEFORE YOU COMMIT" about a commit that had already
   * happened. It survived a reload, so it read as a standing fault rather than
   * a stale screen. Found by walkthrough, 2026-09-05. */
  const pending = chosen !== event.status;

  const missing =
    pending && COMMITTED.includes(chosen)
      ? ([
          event.date === null ? 'events.lifecycle.missing_date' : null,
          event.venue === null ? 'events.lifecycle.missing_venue' : null,
        ].filter(Boolean) as MessageKey[])
      : [];

  /* Settling an Event early is ordinary — teams close the books the same night
   * — so this is said and never refused. A missing date cannot be early. */
  const earlyCompletion =
    pending && chosen === 'completed' && event.date !== null && event.date > today;

  const refused =
    move.error instanceof ApiError && move.error.code === 'entitlement_exceeded'
      ? move.error.fields.find((field) => field.path === 'plan')?.rule
      : null;

  /* Somebody else moved it first. The status it has NOW travels in the fields,
   * so this says something true without a second request — and builds its own
   * sentence, because a message is never parsed. */
  const movedUnderneath =
    move.error instanceof ApiError && move.error.code === 'conflict'
      ? move.error.fields.find((field) => field.path === 'status')?.rule
      : null;

  return (
    <section className="settings__section">
      <h2>{t('events.lifecycle.heading')}</h2>

      <div className="field">
        <label className="field__label" htmlFor={id}>
          {t('events.lifecycle.label')}
        </label>
        <select
          id={id}
          className="field__control"
          value={chosen}
          disabled={move.isPending}
          onChange={(changed) => {
            move.reset();
            setChosen(changed.target.value as EventStatus);
          }}
        >
          {ORDER.map((status) => (
            <option key={status} value={status}>
              {t(LABELS[status])}
            </option>
          ))}
        </select>
      </div>

      {/* Prompted, never blocked. The Save below stays enabled whatever this
          says — that is the decision the story records, not an oversight. */}
      {missing.length > 0 ? (
        <Callout tone="info">
          <strong>{t('events.lifecycle.incomplete')}</strong>
          <ul>
            {missing.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {earlyCompletion ? (
        <Callout tone="info">{t('events.lifecycle.early_completion')}</Callout>
      ) : null}

      {refused ? (
        /* A warning tone, which `Callout` renders with `role="alert"` — so the
            refusal is announced rather than only styled, which the story asks
            for explicitly. */
        <Callout tone="warning">
          {t('events.lifecycle.no_active_events', { plan: planLabel(refused) })}
        </Callout>
      ) : null}

      {movedUnderneath ? (
        <Callout tone="warning">
          {t('events.lifecycle.moved_underneath', {
            status: t(LABELS[movedUnderneath as EventStatus]),
          })}
        </Callout>
      ) : null}

      {move.error && !(move.error instanceof ApiError) ? (
        <Callout tone="danger">
          {move.error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      ) : null}

      <div className="btn-row">
        <Button
          busy={move.isPending}
          disabled={chosen === event.status}
          onClick={() => move.mutate({ from: event.status, to: chosen })}
        >
          {t('events.lifecycle.save')}
        </Button>
      </div>
    </section>
  );
}
